import { ApolloCache, FetchResult, RefetchQueriesInclude } from "@apollo/client";
import { Button, Css, GridTableApi, UseSnackbarHook, useComputed, useGridTableApi, useSnackbar } from "@homebound/beam";
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useRef, useState } from "react";
import {
  AggregateVersionStatus,
  CopyTakeoffLineItemsInput,
  ItivOrderField,
  Order,
  SaveTakeoffLineItemInput,
  TakeoffLineItemFilter,
} from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import useZodQueryString from "src/hooks/useZodQueryString";
import type { TakeoffLineItemRows } from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTable";
import {
  PlanPackageTakeoffTable_GroupTotalsFragment,
  PlanPackageTakeoffTable_GroupTotalsFragmentDoc,
  PlanPackageTakeoffTable_ItemsFragment,
} from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTable.generated";
import {
  PlanPackageTakeoffPage_TakeoffDetailFragment,
  usePlanPackageTakeoffPageQuery,
} from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import {
  TakeoffsStore_CopyTakeoffLineItemsMutation,
  TakeoffsStore_ProposalStatusFragmentDoc,
  TakeoffsStore_SaveTakeoffLineItemsMutation,
  useTakeoffsStore_CopyTakeoffLineItemsMutation,
  useTakeoffsStore_SaveTakeoffLineItemsMutation,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext.generated";
import { isItivOrderField } from "src/routes/libraries/plan-package/takeoffs/utils";
import { count, fail, mergeEntities, queryResult, sortObjectKeys } from "src/utils";
import { toServerFilter } from "src/utils/takeoffLineItem";
import { z } from "zod";
import { StoreApi, createStore, useStore } from "zustand";

export type TakeoffsStoreState = {
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  filter: TakeoffLineItemFilter;
  setFilter: Dispatch<SetStateAction<TakeoffLineItemFilter>>;
  setItemTableSearch: (search: string) => void;
  itemTableGroupBy: ItivOrderField;
  setItemTableGroupBy: (groupBy: ItivOrderField) => void;
  itemTableSortBy: ItivOrderField;
  setItemTableSortBy: (sortBy: ItivOrderField) => void;
  /** V2's header sort can go descending; v1's Sort By dropdown is always ascending */
  itemTableSortDirection: Order;
  itemTableApi: GridTableApi<TakeoffLineItemRows> | undefined;
  saveTli: (item: SaveTakeoffLineItemInput | SaveTakeoffLineItemInput[], opts?: SaveTliOpts) => SaveTliResult;
  copyFromPlan: (input: CopyTakeoffLineItemsInput) => Promise<FetchResult<TakeoffsStore_CopyTakeoffLineItemsMutation>>;
  /** Whether to include removed TLIs - true when viewing a draft or archived version */
  includeRemoved: boolean;
  refetchFilters?: () => void;
  clearFilters: () => void;
  /** The line V2's side panel shows, copied in by `TakeoffsSidePanelProvider` */
  activeTliId?: string;
};

const takeoffsQuerySchema = z.object({
  groupBy: z.coerce.string().default(ItivOrderField.CostCode),
  sortBy: z.coerce.string().default(ItivOrderField.Item),
  filter: z.coerce.string().default("{}"),
});

type TakeoffsQueryString = z.infer<typeof takeoffsQuerySchema>;

export const TakeoffsManagerContext = createContext<StoreApi<TakeoffsStoreState> | null>(null);

export type TakeoffsManagerProviderProps = React.PropsWithChildren<
  { planPackageId: string; versionId: string } & Pick<TakeoffsStoreProviderProps, "syncViewToUrl">
>;

/**
 * The provider app code mounts, on both the v1 takeoffs page and the V2 Takeoffs tab. Loads the plan package's
 * takeoffs, then renders `TakeoffsStoreProvider` with it.
 */
export function TakeoffsManagerProvider(props: TakeoffsManagerProviderProps) {
  const { planPackageId, versionId, syncViewToUrl, children } = props;
  const query = usePlanPackageTakeoffPageQuery({ variables: { planPackageId, rpavId: versionId } });
  return queryResult(query, ({ planPackage }) => (
    <TakeoffsStoreProvider planPackage={planPackage} syncViewToUrl={syncViewToUrl}>
      {children}
    </TakeoffsStoreProvider>
  ));
}

export type TakeoffsStoreProviderProps = React.PropsWithChildren<{
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  /**
   * Whether the store keeps the table's filter, group and sort in the URL, as v1 does. The V2 tab's table layout owns
   * those instead and mirrors them into the store. Defaults to true.
   */
  syncViewToUrl?: boolean;
}>;

/**
 * Builds the store from an already-loaded plan package. App code gets it via `TakeoffsManagerProvider`; mount it
 * directly only in tests and stories that seed a plan package fragment.
 */
export function TakeoffsStoreProvider(props: TakeoffsStoreProviderProps) {
  const { planPackage, syncViewToUrl = true, children } = props;
  const storeRef = useRef<StoreApi<TakeoffsStoreState>>();
  const [{ groupBy: initialGroupBy, sortBy: initialSortBy, filter: initialFilter }, setQs] =
    useZodQueryString(takeoffsQuerySchema);
  const setQsRef = useRef(setQs);
  setQsRef.current = setQs;
  const itemTableApi = useGridTableApi<TakeoffLineItemRows>();
  const { triggerNotice, closeNotice } = useSnackbar();
  const [saveTlis] = useTakeoffsStore_SaveTakeoffLineItemsMutation();
  const [copyFromPlan] = useTakeoffsStore_CopyTakeoffLineItemsMutation();
  const navigateToRpav = useNavigateToRpav();
  // The store keeps the navigateToRpav it was built with, and each one closes over that render's location. Reading it
  // through a ref makes a save navigate from the current URL, so filters applied since aren't dropped.
  const navigateToRpavRef = useRef(navigateToRpav);
  navigateToRpavRef.current = navigateToRpav;

  if (!storeRef.current || storeRef.current.getState().planPackage !== planPackage) {
    // If the plan package version has changed, then clear the selections
    if (planPackage.version.id !== storeRef.current?.getState().planPackage.version.id) {
      itemTableApi.clearSelections();
    }
    /*
      Note that currently just fetching a new page during pagination causes this store to be reinitialized,
      b/c we get a new planPackage fragment from apollo, and we probably should be doing something different.
    */
    storeRef.current = createTakeoffsStore({
      planPackage,
      navigateToRpav: (rpavId) => navigateToRpavRef.current(rpavId),
      ...(syncViewToUrl && {
        initialGroupBy,
        initialSortBy,
        initialFilter,
        setQs: (state: Partial<TakeoffsQueryString>) => setQsRef.current(state),
      }),
      itemTableApi,
      saveTlis,
      copyFromPlan,
      triggerNotice,
      closeNotice,
    });
  }

  return <TakeoffsManagerContext.Provider value={storeRef.current}>{children}</TakeoffsManagerContext.Provider>;
}

// Returns the store for the takeoffs manager context for instances where reactivity is not needed
export function useTakeoffsManagerContext() {
  const store = useContext(TakeoffsManagerContext);
  if (!store) throw new Error("Missing TakeoffsManagerProvider");
  return store;
}

export function useTakeoffsStore<T>(selector: (state: TakeoffsStoreState) => T) {
  const store = useContext(TakeoffsManagerContext);
  if (!store) throw new Error("Missing TakeoffsManagerProvider");
  return useStore(store, selector);
}

export type CreateTakeoffsStoreProps = {
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  /** Swaps the maybe-new rpav into the current URL, keeping its filters and sort */
  navigateToRpav: (rpavId: string) => void;
  initialGroupBy?: string;
  initialSortBy?: string;
  initialFilter?: string;
  setQs?: (state: Partial<TakeoffsQueryString>) => void;
  itemTableApi: GridTableApi<TakeoffLineItemRows>;
  saveTlis: ReturnType<typeof useTakeoffsStore_SaveTakeoffLineItemsMutation>[0];
  copyFromPlan: ReturnType<typeof useTakeoffsStore_CopyTakeoffLineItemsMutation>[0];
  triggerNotice?: UseSnackbarHook["triggerNotice"];
  closeNotice?: UseSnackbarHook["closeNotice"];
};

// Create a store for the takeoffs manager context
// Exported to simplifying testing
export function createTakeoffsStore(props: CreateTakeoffsStoreProps) {
  const {
    planPackage,
    initialGroupBy,
    initialSortBy,
    initialFilter,
    setQs,
    itemTableApi,
    saveTlis,
    copyFromPlan,
    triggerNotice,
    closeNotice,
    navigateToRpav,
  } = props;
  // Include removed TLIs when viewing a draft or archived version, so the table can highlight what changed
  const includeRemoved =
    planPackage.version.status.code === AggregateVersionStatus.Draft ||
    planPackage.version.status.code === AggregateVersionStatus.Archived;

  const store = createStore<TakeoffsStoreState>((set, get) => ({
    // Initial Values
    filter: initialFilter ? JSON.parse(initialFilter) : {},
    itemTableGroupBy: initialGroupBy && isItivOrderField(initialGroupBy) ? initialGroupBy : ItivOrderField.CostCode,
    itemTableSortBy: initialSortBy && isItivOrderField(initialSortBy) ? initialSortBy : ItivOrderField.Item,
    itemTableSortDirection: Order.Asc,
    itemTableApi,
    planPackage,
    includeRemoved,
    // Actions
    setFilter: (maybeFunction: Function | TakeoffLineItemFilter) =>
      set((state) => {
        const filterValue = typeof maybeFunction === "function" ? maybeFunction(state.filter) : maybeFunction;
        setQs?.({ filter: JSON.stringify(filterValue) });
        return { filter: filterValue };
      }),
    setItemTableGroupBy: (groupBy: ItivOrderField) => {
      setQs?.({ groupBy, sortBy: get().itemTableSortBy });
      set({ itemTableGroupBy: groupBy });
    },
    setItemTableSortBy: (sortBy: ItivOrderField) => {
      setQs?.({ sortBy, groupBy: get().itemTableGroupBy });
      set({ itemTableSortBy: sortBy });
    },
    setItemTableSearch: (search: string) =>
      set((state) => {
        // Remove search and don't set a search value it is empty - avoids unnecessary API requests when going from `undefined` to ""
        const { search: oldSearchValue, ...otherFilters } = state.filter;
        return { filter: { ...otherFilters, ...(search ? { search } : {}) } };
      }),
    clearFilters: () => get().setFilter({}),
    saveTli: saveTliImpl,
    copyFromPlan: copyFromPlanImpl,
  }));

  /** Private properties and methods */
  function getCacheQualifier() {
    return JSON.stringify({
      filter: sortObjectKeys({
        ...toServerFilter(planPackage.id, store.getState().filter),
        // Keep in sync with the filter in PlanPackageTakeoffTableQuery, otherwise cache.modify won't find the right entry
        includeRemoved: store.getState().includeRemoved,
      }),
      order: [
        { direction: Order.Asc, field: store.getState().itemTableGroupBy },
        { direction: store.getState().itemTableSortDirection, field: store.getState().itemTableSortBy },
      ],
    });
  }

  async function copyFromPlanImpl(input: CopyTakeoffLineItemsInput) {
    const result = await copyFromPlan({
      variables: { input },
      update: (cache, { data }) => {
        addItemsToCache(cache, data?.copyTakeoffLineItems?.copiedItems ?? []);
      },
    });

    // Navigate to the new draft version
    navigateToRpav(result.data!.copyTakeoffLineItems.targetReadyPlan.version.id);

    return result;
  }

  async function saveTliImpl(
    input: SaveTakeoffLineItemInput | SaveTakeoffLineItemInput[],
    opts: SaveTliOpts = {},
  ): SaveTliResult {
    const requestInput = Array.isArray(input) ? input : [input];
    const result = await saveTlis({
      ...opts,
      variables: {
        readyPlanId: planPackage.id,
        input: requestInput,
        filter: {
          readyPlan: [store.getState().planPackage.id],
          includeRemoved: store.getState().includeRemoved,
          ...store.getState().filter,
        },
        order: [
          { field: store.getState().itemTableGroupBy, direction: Order.Asc },
          { field: store.getState().itemTableSortBy, direction: store.getState().itemTableSortDirection },
        ],
      },
      update: (cache, { data }) => {
        const {
          deleted = [],
          takeoffLineItems = [],
          autoAddedLaborLines = [],
          groupTotals = [],
          removed = [],
        } = data?.saveTakeoffLineItems ?? {};

        if (deleted.nonEmpty) itemTableApi.deleteRows(deleted);
        // A save that deletes or removes lines clears the selection, so it can't count lines that are gone
        if (deleted.nonEmpty || removed.nonEmpty) itemTableApi.clearSelections();

        // show notice if any tli was added that doesn't match filter
        const ppGroupTotal = groupTotals.find((gt) => gt.groupId === "queryTotal") ?? fail("No group totals found");
        // Inputs without an id are the adds; updates and removals of existing lines don't count
        const existingTliIds = new Set(requestInput.map((tli) => tli.id).compact());
        const addedTliIds = takeoffLineItems.map((tli) => tli.id).filter((id) => !existingTliIds.has(id));
        if (!addedTliIds.every((addedTliId) => ppGroupTotal.tliIdsInGroup.includes(addedTliId))) {
          triggerNotice?.({
            // putting the action in the message instead of using the action prop to put it more inline
            message: (
              <>
                <div css={Css.dif.$}>
                  Success! Your item was added, but not showing due to filters. To see your item
                </div>
                <Button
                  label="clear filters"
                  variant="tertiary"
                  onClick={() => {
                    store.getState().clearFilters();
                    closeNotice?.("tli-add-filter-notice");
                  }}
                />
              </>
            ),
            icon: "success",
            id: "tli-add-filter-notice",
          });
        }

        addItemsToCache(cache, [...takeoffLineItems, ...autoAddedLaborLines], groupTotals);

        // Resolved proposals are soft-deleted, so the result doesn't return them; acknowledge what we sent instead.
        // Open question: this copies LocationsTab's `PlanPackageLocationProposalStatus` write. Saves could return the
        // proposals they resolve instead, like `resolveAiFieldProposals` does, but that should change for every
        // `proposals` sidecar at once so they all handle proposals the same way.
        requestInput
          .flatMap((tli) => tli.proposals ?? [])
          .forEach(({ id, status }) =>
            cache.writeFragment({
              id: cache.identify({ __typename: "AiFieldProposal", id }),
              fragment: TakeoffsStore_ProposalStatusFragmentDoc,
              data: {
                __typename: "AiFieldProposal",
                id,
                status: { __typename: "AiFieldProposalStatusDetail", code: status },
              },
            }),
          );
      },
    });

    // Navigate to the new draft version
    navigateToRpav(result.data!.saveTakeoffLineItems.version.id);

    const autoAddedLaborLines = result.data?.saveTakeoffLineItems.autoAddedLaborLines ?? [];
    if (autoAddedLaborLines.nonEmpty) {
      triggerNotice?.({
        message: `${count(autoAddedLaborLines, "labor line")} automatically added due to new materials.`,
      });
    }

    return result;
  }

  function addItemsToCache(
    cache: ApolloCache,
    newTlis: PlanPackageTakeoffTable_ItemsFragment[],
    groupTotalsResult?: PlanPackageTakeoffTable_GroupTotalsFragment[],
  ) {
    const qualifier = getCacheQualifier();
    cache.modify({
      fields: {
        [`takeoffLineItemsPage:${qualifier}`]: (
          existingResult: any, // Modifiers<TakeoffLineItemVersionsPageQuery["TakeoffLineItemVersionsPage"]>, //, TODO: Fix this type?
        ) => {
          const { items: existingTlis } = existingResult;

          const newOrUpdatedGroups = groupTotalsResult?.map((gt) => {
            const __ref = cache.identify(gt);
            cache.writeFragment({ id: __ref, fragment: PlanPackageTakeoffTable_GroupTotalsFragmentDoc, data: gt });
            return { __ref };
          });

          // Then the saved entities should be added to the tliPage cache
          return {
            ...existingResult,
            items: mergeEntities(
              existingTlis,
              newTlis.map((tli) => ({ __ref: cache.identify(tli) })),
            ),
            groupTotals: newOrUpdatedGroups,
          };
        },
      },
    });
    // Refetch filters to ensure they only include facets that are still relevant
    store.getState().refetchFilters?.();
  }

  return store;
}

export type SaveTliResult = Promise<FetchResult<TakeoffsStore_SaveTakeoffLineItemsMutation>>;

/** For saves that change data outside the takeoffs table, like the page's AI badge. */
type SaveTliOpts = { refetchQueries?: RefetchQueriesInclude };

export function useSelectedTliIds(itemTableApi: GridTableApi<TakeoffLineItemRows> | undefined) {
  const [debugMode, setDebugMode] = useState(false);
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "-") {
        setDebugMode(true);
      }
    };

    // Add event listener when the component mounts
    window.addEventListener("keydown", handleKeyPress);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  useComputed(() => {
    if (debugMode) {
      console.log("rows from table api", itemTableApi?.getSelectedRows());
      console.log(
        "expanded rows",
        itemTableApi
          ?.getSelectedRows()
          .flatMap((row) => {
            switch (row.kind) {
              case "item":
                return row.data.id;
              case "groupBy":
                console.log("groupBy row", row.id, row.data.activeTliIdsInGroup);
                return row.data.activeTliIdsInGroup;
              default:
                return [];
            }
          })
          .unique() ?? [],
      );
    }
  }, [itemTableApi, debugMode]);

  return useComputed(
    () =>
      itemTableApi
        ?.getSelectedRows()
        .flatMap((row) => {
          switch (row.kind) {
            case "item":
              return row.data.id;
            case "groupBy":
              return row.data.activeTliIdsInGroup;
            default:
              return [];
          }
        })
        .unique() ?? [],
    [itemTableApi],
  );
}
